import { useState, useEffect, useCallback } from 'react';
import { keysApi } from '../services/keysApi';

const KEY_PROVIDERS = [
  { provider: 'pollo', stateKey: 'polloKey', storageKey: 'pollo_api_key' },
  { provider: 'runninghub', stateKey: 'runningHubKey', storageKey: 'runninghub_api_key' },
  { provider: 'grsai', stateKey: 'grsaiKey', storageKey: 'grsai_api_key' },
  { provider: 'fal', stateKey: 'falKey', storageKey: 'fal_api_key' },
  { provider: 'agnes', stateKey: 'agnesKey', storageKey: 'agnes_api_key' },
  { provider: 'wavespeed', stateKey: 'wavespeedKey', storageKey: 'wavespeed_api_key' },
  { provider: 'replicate', stateKey: 'replicateKey', storageKey: 'replicate_api_key' },
] as const;

export function useApiKeyManager(isOpen: boolean) {
  const [polloKey, setPolloKey] = useState('');
  const [runningHubKey, setRunningHubKey] = useState('');
  const [grsaiKey, setGrsaiKey] = useState('');
  const [falKey, setFalKey] = useState('');
  const [agnesKey, setAgnesKey] = useState('');
  const [wavespeedKey, setWavespeedKey] = useState('');
  const [replicateKey, setReplicateKey] = useState('');
  const [savedMasked, setSavedMasked] = useState<Record<string, string>>({});
  const [isSaved, setIsSaved] = useState(false);
  const [saveError, setSaveError] = useState('');

  const keySetters: Record<string, (v: string) => void> = {
    polloKey: setPolloKey,
    runningHubKey: setRunningHubKey,
    grsaiKey: setGrsaiKey,
    falKey: setFalKey,
    agnesKey: setAgnesKey,
    wavespeedKey: setWavespeedKey,
    replicateKey: setReplicateKey,
  };

  const keyGetters: Record<string, string> = {
    polloKey: polloKey,
    runningHubKey: runningHubKey,
    grsaiKey: grsaiKey,
    falKey: falKey,
    agnesKey: agnesKey,
    wavespeedKey: wavespeedKey,
    replicateKey: replicateKey,
  };

  const refreshSavedMasked = useCallback(async () => {
    try {
      const entries = await keysApi.listKeys();
      const masked: Record<string, string> = {};
      for (const entry of entries) {
        if (entry.keySuffix) masked[entry.provider] = entry.keySuffix;
      }
      setSavedMasked(masked);
      return true;
    } catch {
      setSavedMasked({});
      return false;
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    for (const { storageKey, stateKey } of KEY_PROVIDERS) {
      localStorage.removeItem(storageKey);
      keySetters[stateKey]('');
    }
    setSaveError('');
    void refreshSavedMasked();
  }, [isOpen, refreshSavedMasked]);

  const handleSave = useCallback(async () => {
    const failures: string[] = [];
    for (const { provider, stateKey } of KEY_PROVIDERS) {
      const key = keyGetters[stateKey].trim();
      if (!key) continue;
      try {
        await keysApi.addKey({ provider, key });
        keySetters[stateKey]('');
      } catch {
        failures.push(provider);
      }
    }
    if (failures.length > 0) {
      setSaveError(`以下服务商的 Key 保存失败，请确认登录与管理员权限后重试：${failures.join('、')}`);
      return;
    }
    setSaveError('');
    await refreshSavedMasked();
    setIsSaved(true);
    setTimeout(() => setIsSaved(false), 2000);
  }, [polloKey, runningHubKey, grsaiKey, falKey, agnesKey, wavespeedKey, replicateKey, refreshSavedMasked]);

  return {
    polloKey, setPolloKey,
    runningHubKey, setRunningHubKey,
    grsaiKey, setGrsaiKey,
    falKey, setFalKey,
    agnesKey, setAgnesKey,
    wavespeedKey, setWavespeedKey,
    replicateKey, setReplicateKey,
    savedMasked,
    isSaved,
    saveError,
    handleSave,
  };
}
